import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { adminEndPoll, adminLogin, adminLogout, adminReopenPoll, adminResults, adminSession, adminPublishResults, adminSetClose, getStatus, } from "./api";
export function Admin() {
    const [authed, setAuthed] = useState(null);
    useEffect(() => {
        adminSession()
            .then((s) => setAuthed(s.authenticated))
            .catch(() => setAuthed(false));
    }, []);
    return (_jsx("div", { className: "admin", children: authed === null ? (_jsx("div", { className: "container", children: _jsx("p", { className: "muted", children: "Loading\u2026" }) })) : !authed ? (_jsx(Login, { onSuccess: () => setAuthed(true) })) : (_jsx(Dashboard, { onLogout: () => setAuthed(false) })) }));
}
function Login({ onSuccess }) {
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    async function submit(e) {
        e.preventDefault();
        setError(null);
        setPending(true);
        try {
            await adminLogin(password);
            onSuccess();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setPending(false);
        }
    }
    return (_jsx("div", { className: "container", children: _jsxs("div", { className: "card", children: [_jsx("h1", { children: "Admin login" }), _jsxs("form", { onSubmit: submit, children: [_jsx("input", { type: "password", value: password, onChange: (e) => setPassword(e.target.value), placeholder: "Password", autoFocus: true }), _jsx("div", { className: "row", style: { marginTop: 12 }, children: _jsx("button", { type: "submit", className: "primary", disabled: pending, children: pending ? "Signing in..." : "Sign in" }) }), error && _jsx("p", { className: "error", children: error })] })] }) }));
}
function Dashboard({ onLogout }) {
    const [data, setData] = useState(null);
    const [closesAt, setClosesAt] = useState(null);
    const [resultsPublishedAt, setResultsPublishedAt] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    async function refresh() {
        try {
            const [results, status] = await Promise.all([adminResults(), getStatus()]);
            setData(results);
            setClosesAt(status.closesAt);
            setResultsPublishedAt(status.resultsPublishedAt);
            setError(null);
        }
        catch (e) {
            setError(e.message);
        }
    }
    async function setCloseTime(localValue) {
        setBusy(true);
        try {
            const iso = localValue ? new Date(localValue).toISOString() : null;
            const res = await adminSetClose(iso);
            setClosesAt(res.closesAt);
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setBusy(false);
        }
    }
    async function publishResults() {
        if (!confirm("Publish results now? This closes the poll, makes /results public, and emails verified subscribers.")) {
            return;
        }
        setBusy(true);
        try {
            const res = await adminPublishResults();
            setResultsPublishedAt(res.publishedAt);
            await refresh();
            alert(`Published. ${res.notifiedSubscribers} email(s) sent. ${res.failedSubscribers} failed.`);
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setBusy(false);
        }
    }
    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 10_000);
        return () => clearInterval(id);
    }, []);
    async function endPoll() {
        if (!confirm("End the poll? Public visitors will no longer be able to respond."))
            return;
        setBusy(true);
        try {
            await adminEndPoll();
            await refresh();
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setBusy(false);
        }
    }
    async function reopenPoll() {
        setBusy(true);
        try {
            await adminReopenPoll();
            await refresh();
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setBusy(false);
        }
    }
    async function logout() {
        await adminLogout();
        onLogout();
    }
    if (!data && !error) {
        return (_jsx("div", { className: "container wide", children: _jsx("p", { className: "muted", children: "Loading results..." }) }));
    }
    if (error && !data) {
        return (_jsx("div", { className: "container wide", children: _jsx("div", { className: "card", children: _jsx("p", { className: "error", children: error }) }) }));
    }
    if (!data)
        return null;
    return (_jsxs("div", { className: "container wide", children: [_jsx("div", { className: "card", children: _jsxs("div", { className: "row", style: { justifyContent: "space-between" }, children: [_jsxs("div", { children: [_jsx("h1", { style: { margin: 0 }, children: "Admin dashboard" }), _jsxs("div", { className: "row", style: { marginTop: 8 }, children: [_jsx("span", { className: `pill ${data.status === "open" ? "open" : "closed"}`, children: data.status }), _jsxs("span", { className: "muted", children: [data.totalResponses, " responses"] }), resultsPublishedAt && (_jsx("span", { className: "pill", style: { borderColor: "var(--accent-dark)", color: "var(--accent-dark)" }, children: "results published" }))] })] }), _jsxs("div", { className: "row", children: [data.status === "open" ? (_jsx("button", { className: "danger", onClick: endPoll, disabled: busy, children: "End poll" })) : (_jsx("button", { className: "ghost", onClick: reopenPoll, disabled: busy, children: "Reopen poll" })), !resultsPublishedAt && (_jsx("button", { className: "primary", onClick: publishResults, disabled: busy, children: "Publish results" })), _jsx("a", { className: "ghost", href: "/api/admin/export.csv", download: true, children: "Export CSV" }), _jsx("button", { className: "ghost", onClick: refresh, children: "Refresh" }), _jsx("button", { className: "ghost", onClick: logout, children: "Log out" })] })] }) }), error && (_jsx("div", { className: "card", children: _jsx("p", { className: "error", children: error }) })), _jsx(CloseTimeCard, { closesAt: closesAt, busy: busy, onSet: setCloseTime }), _jsxs("div", { className: "grid-2", children: [_jsx(OverallCard, { data: data }), _jsx(ConfidenceCard, { avg: data.overall.averageConfidence })] }), _jsx(GroupTable, { title: "Personal choice by frame", rows: data.byFrame, keyHeader: "Frame" }), _jsx(GroupTable, { title: "By children/infirm salience", rows: data.bySalience, keyHeader: "Salience" }), _jsx(GroupTable, { title: "By label condition", rows: data.byLabelCondition, keyHeader: "Labels" }), _jsx(GroupTable, { title: "By clause order", rows: data.byOrderCondition, keyHeader: "Order" }), _jsx(CrossTab, { title: "Personal vs public recommendation", rows: data.crossTabs.personalVsPublic }), _jsx(CrossTab, { title: "Personal vs child/dependent recommendation", rows: data.crossTabs.personalVsDependent })] }));
}
function OverallCard({ data }) {
    const o = data.overall;
    return (_jsxs("div", { className: "card", children: [_jsx("h2", { children: "Overall" }), _jsx(ChoiceBar, { label: "Personal choice", totals: o.personalChoice }), _jsx(ChoiceBar, { label: "Public recommendation", totals: o.publicRecommendation }), _jsx(ChoiceBar, { label: "Dependent recommendation", totals: o.dependentRecommendation }), _jsx(ChoiceBar, { label: "Expected majority", totals: o.expectedMajority })] }));
}
function ConfidenceCard({ avg }) {
    return (_jsxs("div", { className: "card", children: [_jsx("h2", { children: "Average confidence" }), _jsx("p", { style: { fontSize: 36, margin: 0 }, children: avg.toFixed(2) }), _jsx("p", { className: "muted", children: "Scale 1 (not confident) \u2013 5 (extremely confident)." })] }));
}
function ChoiceBar({ label, totals }) {
    const n = totals.threshold + totals.safe;
    const tPct = n === 0 ? 0 : (totals.threshold / n) * 100;
    const sPct = n === 0 ? 0 : (totals.safe / n) * 100;
    return (_jsxs("div", { style: { marginBottom: 12 }, children: [_jsxs("div", { className: "row", style: { justifyContent: "space-between" }, children: [_jsx("span", { style: { fontWeight: 600 }, children: label }), _jsxs("span", { className: "muted", children: ["n = ", n] })] }), _jsxs("div", { className: "bar", title: `threshold ${tPct.toFixed(1)}% / safe ${sPct.toFixed(1)}%`, children: [_jsx("span", { className: "b-threshold", style: { width: `${tPct}%` }, children: tPct >= 8 ? `${tPct.toFixed(0)}%` : "" }), _jsx("span", { className: "b-safe", style: { width: `${sPct}%` }, children: sPct >= 8 ? `${sPct.toFixed(0)}%` : "" })] }), _jsxs("div", { className: "muted", style: { fontSize: 12, marginTop: 4 }, children: ["threshold ", totals.threshold, " \u00B7 safe ", totals.safe] })] }));
}
function GroupTable({ title, rows, keyHeader, }) {
    return (_jsxs("div", { className: "card", children: [_jsx("h2", { children: title }), _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: keyHeader }), _jsx("th", { children: "N" }), _jsx("th", { children: "Personal threshold %" }), _jsx("th", { children: "Public threshold %" }), _jsx("th", { children: "Dependent threshold %" }), _jsx("th", { children: "Avg confidence" })] }) }), _jsx("tbody", { children: rows.map((r) => (_jsxs("tr", { children: [_jsx("td", { children: r.key }), _jsx("td", { children: r.n }), _jsx("td", { children: thresholdPct(r.personalChoice) }), _jsx("td", { children: thresholdPct(r.publicRecommendation) }), _jsx("td", { children: thresholdPct(r.dependentRecommendation) }), _jsx("td", { children: r.n === 0 ? "—" : r.averageConfidence.toFixed(2) })] }, r.key))) })] })] }));
}
function thresholdPct(t) {
    const n = t.threshold + t.safe;
    if (n === 0)
        return "—";
    return `${((t.threshold / n) * 100).toFixed(1)}%`;
}
function CrossTab({ title, rows }) {
    return (_jsxs("div", { className: "card", children: [_jsx("h2", { children: title }), _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Pattern" }), _jsx("th", { children: "Count" }), _jsx("th", { children: "Percent" })] }) }), _jsx("tbody", { children: rows.map((r) => (_jsxs("tr", { children: [_jsx("td", { children: r.pattern }), _jsx("td", { children: r.count }), _jsxs("td", { children: [r.percent, "%"] })] }, r.pattern))) })] })] }));
}
function CloseTimeCard({ closesAt, busy, onSet, }) {
    const [draft, setDraft] = useState("");
    // Pre-fill the input with the current scheduled close, in the local-time
    // format <input type="datetime-local"> wants.
    useEffect(() => {
        if (!closesAt) {
            setDraft("");
            return;
        }
        const d = new Date(closesAt);
        if (isNaN(d.valueOf())) {
            setDraft("");
            return;
        }
        setDraft(toLocalDatetimeInput(d));
    }, [closesAt]);
    return (_jsxs("div", { className: "card", children: [_jsx("h2", { children: "Scheduled close" }), _jsx("p", { className: "muted", style: { marginTop: 0 }, children: "Set a future timestamp. The poll will auto-close at that moment; participants see a live countdown until then." }), _jsxs("div", { className: "row", style: { alignItems: "flex-end", flexWrap: "wrap" }, children: [_jsx("input", { type: "datetime-local", value: draft, onChange: (e) => setDraft(e.target.value), style: { padding: "10px 12px", border: "1px solid var(--rule-strong)", background: "var(--panel-3)", color: "var(--ink)", fontFamily: "inherit", fontSize: "14px" } }), _jsx("button", { className: "primary", disabled: busy || !draft, onClick: () => onSet(draft || null), children: "Set close time" }), closesAt && (_jsx("button", { className: "ghost", disabled: busy, onClick: () => onSet(null), children: "Clear" }))] }), closesAt && (_jsxs("p", { className: "muted", style: { marginTop: 12 }, children: ["Currently set to ", new Date(closesAt).toLocaleString(), "."] }))] }));
}
function toLocalDatetimeInput(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return (`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}`);
}
